"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Minimize2, Maximize2, Coffee, AlertTriangle } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  getBreakState,
  setBreakState,
  clearBreakState,
  keyDoUsuario,
  BREAK_EVENT,
  type ActiveBreakState,
} from "@/lib/chat/break-state";
import {
  MY_AVAILABILITY_EVENT,
  emitMyAvailability,
  type Availability,
} from "@/lib/chat/my-availability";
import { applyBreakReconciliation, applyBreakEvent } from "@/lib/chat/reconcile-break";
import { onPresenceEvent } from "@/hooks/use-presence-connection";

interface StatusSnapshot {
  availability: Availability;
  activeBreak: { breakReasonName: string | null; startedAt: string; maxSeconds?: number | null } | null;
}
import { formatBreakElapsed } from "@/lib/chat/format-break-elapsed";
import {
  breakLimit,
  formatClock,
  formatLimitMinutes,
  formatOverrunLong,
  type BreakLimitState,
} from "@/lib/chat/break-limit";

// Por usuario, como a propria pausa (#538): num navegador compartilhado o aviso
// de um atendente nao deve decidir o toast de outro.
function chaveDoAvisoDeEstouro(): string | null {
  const base = keyDoUsuario();
  return base ? `${base}:overrun-warned` : null;
}
function lerAvisoDeEstouro(): string | null {
  const key = chaveDoAvisoDeEstouro();
  if (!key) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function gravarAvisoDeEstouro(chave: string): void {
  const key = chaveDoAvisoDeEstouro();
  if (!key) return;
  try {
    localStorage.setItem(key, chave);
  } catch {
    // sem localStorage: no pior caso o aviso se repete num F5
  }
}

// Mesmo threshold do drag do webphone (webphone-sticky.tsx) — distingue
// clique de arrasto.
const DRAG_THRESHOLD_PX = 5;

/**
 * Indicador global de pausa do atendente. Enquanto em pausa mostra um overlay
 * central (que pode ser minimizado pra uma faixa no topo). Some ao encerrar.
 * Estado em localStorage (break-state) — sobrevive navegação/reload.
 */
export function BreakBanner() {
  const [brk, setBrk] = useState<ActiveBreakState | null>(null);
  const [, setTick] = useState(0); // força re-render do contador a cada 1s
  const [ending, setEnding] = useState(false);

  // Sincroniza com o localStorage (start/end vindos do seletor de status, ou
  // de outra aba via 'storage').
  useEffect(() => {
    const sync = () => setBrk(getBreakState());
    sync();
    window.addEventListener(BREAK_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(BREAK_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // #450: o indicador segue o que o MOTOR reporta, nao o estado local. Duas
  // fontes, um so caminho de escrita (applyBreakReconciliation):
  //
  // 1) Boot — o estado em localStorage sobrevive ao logout, e o motor encerra
  //    pausa por caminhos que nao emitem evento (logout via `update_all`,
  //    CleanupStaleBreakSessionsJob). Sem reler o snapshot, uma pausa morta na
  //    vespera reaparece contando: era o "em pausa ha 16h" da TCNET com o
  //    agente ja online e recebendo conversa.
  // 2) Realtime — #102: fim de pausa chega no payload de presenca, sem GET.
  //
  // Falha de rede e 5xx nao mexem no estado: sem resposta do motor a
  // disponibilidade e DESCONHECIDA, e desconhecido preserva. O 404 (usuario sem
  // ChatAgentSync) NAO e desconhecido — e resposta: quem nao e agente nao pausa,
  // e o estado e limpo (#538; preservar aqui foi o que vazou a pausa de um
  // usuario para outro no mesmo navegador).
  useEffect(() => {
    let cancelled = false;
    api
      .request<StatusSnapshot>("/chat/me/availability")
      .then((snap) => {
        if (cancelled) return;
        // gk-chat#280: `activeBreak` deixou de ser sempre nulo — o backend o
        // reconstroi do evento `agent.break.updated`. E o que da duracao pra
        // quem abre a plataforma ja em pausa (comportamento novo do login).
        applyBreakReconciliation(snap.availability, {
          reason: snap.activeBreak?.breakReasonName ?? null,
          startedAt: snap.activeBreak?.startedAt ?? null,
          maxSeconds: snap.activeBreak?.maxSeconds,
        });
      })
      .catch((err) => {
        // #538: 404 = este usuario nao tem agente no motor. E resposta, nao
        // silencio — quem nao e agente nao pausa, e o estado local dele (se
        // houver) nao descreve pausa nenhuma. Rede/5xx seguem preservando
        // (#450: sem resposta, a disponibilidade e desconhecida).
        if (!cancelled && err instanceof ApiError && err.status === 404) clearBreakState();
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // gk-chat#280: o ciclo de pausa chega pelo socket de PRESENÇA, que é o que
  // existe fora das telas de chat — e o banner é global, montado no layout do
  // dashboard. O `useChatSocket` também escuta, para quem está no Chat; os dois
  // aplicam o mesmo reducer, que é no-op quando nada muda.
  useEffect(() => onPresenceEvent("chat:agent:break", applyBreakEvent), []);

  useEffect(() => {
    const onMyAvailability = (e: Event) => {
      const av = (e as CustomEvent<{ availability: Availability }>).detail?.availability;
      applyBreakReconciliation(av ?? null);
    };
    window.addEventListener(MY_AVAILABILITY_EVENT, onMyAvailability);
    return () => window.removeEventListener(MY_AVAILABILITY_EVENT, onMyAvailability);
  }, []);

  // Tick do contador
  useEffect(() => {
    if (!brk) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [brk]);

  const endBreak = useCallback(async () => {
    if (ending) return;
    setEnding(true);
    try {
      await api.request("/chat/me/breaks/end", { method: "POST" });
      clearBreakState();
      emitMyAvailability("online");
      toast.success("Pausa encerrada.");
    } catch {
      toast.error("Falha ao encerrar pausa.");
    } finally {
      setEnding(false);
    }
  }, [ending]);

  const setMinimized = useCallback(
    (minimized: boolean) => {
      if (!brk) return;
      setBreakState({ ...brk, minimized });
    },
    [brk],
  );

  // Toast UMA vez por pausa, no instante em que passa do limite (mockup). A
  // chave é a pausa (motivo + início) e fica no localStorage: F5, remontagem do
  // layout ou outra aba aberta não repetem o aviso da mesma pausa.
  const elapsedSeg = brk?.startedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(brk.startedAt).getTime()) / 1000))
    : null;
  const limite: BreakLimitState =
    elapsedSeg == null ? breakLimit(0, null) : breakLimit(elapsedSeg, brk?.maxSeconds);
  const chaveDaPausa = brk ? `${brk.reason ?? ""}|${brk.startedAt ?? ""}` : null;
  useEffect(() => {
    if (limite.phase !== "excedida" || !chaveDaPausa || !brk?.maxSeconds) return;
    if (lerAvisoDeEstouro() === chaveDaPausa) return;
    gravarAvisoDeEstouro(chaveDaPausa);
    toast.error("Você passou do limite da pausa", {
      description: `${brk.reason || "Esta pausa"} permite ${formatLimitMinutes(brk.maxSeconds)}. Encerre a pausa para voltar a receber conversas.`,
    });
  }, [limite.phase, chaveDaPausa, brk?.reason, brk?.maxSeconds]);

  if (!brk) return null;

  const elapsed = formatBreakElapsed(brk.startedAt, Date.now());
  const reason = brk.reason || "Pausa";

  // Faixa minimizada — nasce topo-centro, arrastável livremente pela tela.
  if (brk.minimized) {
    return (
      <MinimizedBreakPill
        reason={reason}
        elapsed={elapsed}
        limite={limite}
        maxSeconds={brk.maxSeconds ?? null}
        ending={ending}
        onExpand={() => setMinimized(false)}
        onEnd={endBreak}
      />
    );
  }

  // Overlay central — escurece a tela e centraliza o card. Três visuais
  // (mockup "O que o atendente vê"): dentro do limite, faltando pouco, excedida.
  const excedida = limite.phase === "excedida";
  const perto = limite.phase === "perto";
  const tom = excedida ? "destructive" : perto ? "warning" : "primary";
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-[2px]">
      <div
        data-break-phase={limite.phase}
        className={cn(
          "w-[340px] rounded-2xl border bg-card p-6 text-center shadow-2xl",
          excedida ? "border-destructive ring-4 ring-destructive/15" : perto ? "border-warning/40" : "border-border",
        )}
      >
        <div
          className={cn(
            "mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full",
            tom === "destructive" ? "bg-destructive/15" : tom === "warning" ? "bg-warning/15" : "bg-primary/15",
          )}
        >
          {excedida ? (
            <AlertTriangle className="h-8 w-8 text-destructive" />
          ) : (
            <Coffee className={cn("h-8 w-8", perto ? "text-warning" : "text-primary")} />
          )}
        </div>
        <h2 className="text-lg font-semibold">{excedida ? "Pausa excedida" : "Você está em pausa"}</h2>
        <div
          className={cn(
            "mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
            excedida
              ? "bg-destructive/15 text-destructive"
              : perto
                ? "bg-warning/15 text-warning"
                : "bg-secondary text-secondary-foreground",
          )}
        >
          {excedida && brk.maxSeconds ? `${reason} · limite de ${formatLimitMinutes(brk.maxSeconds)}` : reason}
        </div>
        {elapsed ? (
          <div
            className={cn(
              "my-5 font-mono text-4xl font-bold tabular-nums tracking-tight",
              excedida && "text-destructive",
              perto && "text-warning",
            )}
          >
            {elapsed}
          </div>
        ) : (
          // Pausa declarada pelo motor sem `started_at` (iniciada em outra
          // sessao/dispositivo). Ate o motor publicar o inicio (gk-chat#280),
          // o card assume o desconhecido em vez de contar do zero.
          <div className="my-5 text-sm text-muted-foreground">
            Iniciada em outra sessão
          </div>
        )}
        {limite.phase !== "livre" && brk.maxSeconds ? (
          <div className="mb-5 flex flex-col gap-1.5">
            <div className="relative flex h-1 w-full items-center overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full rounded-full transition-[width]",
                  excedida ? "bg-destructive" : perto ? "bg-warning" : "bg-primary",
                )}
                style={{ width: `${Math.round(limite.fracao * 100)}%` }}
              />
            </div>
            <div
              className={cn(
                "text-left text-xs",
                excedida ? "font-semibold text-destructive" : perto ? "font-medium text-warning" : "text-muted-foreground",
              )}
            >
              {excedida
                ? formatOverrunLong(limite.excedidoSeg)
                : `Limite de ${formatLimitMinutes(brk.maxSeconds)} · restam ${formatClock(limite.restanteSeg)}`}
            </div>
          </div>
        ) : null}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={endBreak}
            disabled={ending}
            className={cn(
              "w-full rounded-lg py-2.5 text-sm font-semibold transition-colors disabled:opacity-60",
              excedida
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : "bg-primary text-primary-foreground hover:bg-primary/90",
            )}
          >
            {ending ? "Encerrando…" : "Encerrar pausa"}
          </button>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-sm text-muted-foreground transition-colors hover:bg-muted"
          >
            <Minimize2 className="h-4 w-4" />
            Minimizar
          </button>
        </div>
      </div>
    </div>
  );
}

interface MinimizedBreakPillProps {
  reason: string;
  elapsed: string | null;
  limite: BreakLimitState;
  maxSeconds: number | null;
  ending: boolean;
  onExpand: () => void;
  onEnd: () => void;
}

/**
 * Pill arrastável — mesmo padrão de drag do WebphoneBubble (Pointer Events +
 * threshold + clamp na viewport), adaptado pra ancoragem top/left porque a
 * posição padrão é centralizada via CSS (não um canto fixo em px como o
 * webphone). Sem persistência: cada pausa nova volta pro topo-centro.
 */
function MinimizedBreakPill({ reason, elapsed, limite, maxSeconds, ending, onExpand, onEnd }: MinimizedBreakPillProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef<{
    startX: number;
    startY: number;
    startPos: { top: number; left: number };
    width: number;
    height: number;
    pointerId: number;
  } | null>(null);
  const movedRef = useRef(false);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = draggingRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!movedRef.current && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
      movedRef.current = true;
    }
    const maxLeft = Math.max(0, window.innerWidth - d.width);
    const maxTop = Math.max(0, window.innerHeight - d.height);
    setPos({
      top: Math.min(Math.max(0, d.startPos.top + dy), maxTop),
      left: Math.min(Math.max(0, d.startPos.left + dx), maxLeft),
    });
  }, []);

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      const d = draggingRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      draggingRef.current = null;
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    },
    [onPointerMove],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // só botão esquerdo / toque primário
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // No primeiro arrasto converte de "centralizado por CSS" pra "top/left em
    // px" usando a posição já renderizada — sem isso o pill saltaria.
    const startPos = pos ?? { top: rect.top, left: rect.left };
    movedRef.current = false;
    draggingRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPos,
      width: rect.width,
      height: rect.height,
      pointerId: e.pointerId,
    };
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  // O pointerdown do arrasto começa em cima dos botões (Expandir/Encerrar)
  // também — como a posição segue o cursor 1:1, o ponteiro solta exatamente
  // sobre o botão e o click nativo dispara mesmo após um arrasto. Por isso,
  // igual ao WebphoneBubble, ignora o click quando houve movimento acima do
  // threshold.
  const guardedClick = (fn: () => void) => () => {
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    fn();
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed z-[60] cursor-grab select-none active:cursor-grabbing",
        pos === null && "top-3 left-1/2 -translate-x-1/2",
      )}
      style={{ ...(pos ? { top: pos.top, left: pos.left } : undefined), touchAction: "none" }}
      onPointerDown={onPointerDown}
    >
      {limite.phase === "excedida" ? (
        <div
          data-break-phase="excedida"
          className="flex items-center gap-2 rounded-full bg-destructive px-3 py-1.5 text-destructive-foreground shadow-lg animate-[gk-over-pulse_1.8s_ease-in-out_infinite] motion-reduce:animate-none"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span className="text-xs font-semibold">Pausa excedida</span>
          <span className="text-xs opacity-90">· {reason}</span>
          <span className="font-mono text-xs font-bold tabular-nums">· +{formatClock(limite.excedidoSeg)}</span>
          <button
            type="button"
            onClick={guardedClick(onExpand)}
            className="ml-1 rounded p-0.5 hover:bg-white/20"
            title="Expandir"
            aria-label="Expandir"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={guardedClick(onEnd)}
            disabled={ending}
            className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold hover:bg-white/30 disabled:opacity-60"
          >
            {ending ? "..." : "Encerrar"}
          </button>
        </div>
      ) : (
        <div
          data-break-phase={limite.phase}
          className={cn(
            "flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 shadow-lg",
            limite.phase === "perto" ? "border-warning/45" : "border-border",
          )}
        >
          <Pause
            className={cn("h-3.5 w-3.5 shrink-0", limite.phase === "perto" ? "text-warning" : "text-muted-foreground")}
            fill="currentColor"
          />
          <span className="text-xs font-medium">Em pausa</span>
          <span className="text-xs text-muted-foreground">· {reason}</span>
          {elapsed && <span className="font-mono text-xs tabular-nums">· {elapsed}</span>}
          {limite.phase === "perto" ? (
            <span className="text-xs font-medium text-warning">
              faltam {Math.max(1, Math.ceil(limite.restanteSeg / 60))} min
            </span>
          ) : limite.phase === "dentro" && maxSeconds ? (
            <span className="text-xs text-muted-foreground">de {formatLimitMinutes(maxSeconds)}</span>
          ) : null}
          <button
            type="button"
            onClick={guardedClick(onExpand)}
            className="ml-1 rounded p-0.5 text-muted-foreground hover:bg-muted"
            title="Expandir"
            aria-label="Expandir"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={guardedClick(onEnd)}
            disabled={ending}
            className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-secondary-foreground hover:bg-secondary/80 disabled:opacity-60"
          >
            {ending ? "..." : "Encerrar"}
          </button>
        </div>
      )}
    </div>
  );
}
